import { Link } from "react-router-dom";
import { CreditCard } from "lucide-react";

import "../styles/landing-luxury.css";
import "../styles/phase2-public.css";

import DocsCategoryLayout from "../components/docs/DocsCategoryLayout";
import { Steps, Tip, DocsTable } from "../components/docs/DocsBits";

const ARTICLES = [
  {
    id: "understanding-plans",
    title: "Understanding your plan",
    body: (
      <>
        <p>Fieldz AI offers three single-location tiers (Standard / Pro / Premium) and dedicated multi-location plans for chains. Every plan includes a 7-day free trial — no credit card required.</p>
        <h3>Single-location plans at a glance</h3>
        <p style={{ fontSize: 13, fontStyle: "italic", color: "var(--lux-text-3)", marginBottom: 8 }}>
          Note: This table is pending owner verification against the live plan config — final values will land in a follow-up.
        </p>
        <DocsTable
          headers={["Feature", "Standard", "Pro", "Premium"]}
          rows={[
            ["Price", "$69 / mo", "$149 / mo", "$249 / mo"],
            ["Users", "1", "Up to 5", "Up to 10"],
            ["Dedicated phone lines", "1", "2", "3"],
            ["SMS allotment / mo", "1,000", "2,500", "5,000"],
            ["Voice minutes / mo", "750", "1,500", "3,000"],
            ["Customer portal", "✓", "✓", "✓"],
            ["IVR + call recording", "—", "✓", "✓"],
            ["Multi-location", "—", "—", "—"],
            ["Priority support", "—", "—", "✓"],
          ]}
          testid="plans-comparison"
        />
        <p>If your team or call volume grows beyond Premium&apos;s allotments, additional SMS and minutes can be topped up à la carte from <strong>Settings &gt; Phones</strong>.</p>
        <Tip>Run on Standard for a few weeks before deciding. Most single-tech businesses never need more — and you can upgrade in one click whenever you do.</Tip>
      </>
    ),
  },
  {
    id: "upgrade-downgrade",
    title: "Upgrading or downgrading",
    body: (
      <>
        <p>Plan changes are instant in either direction, with prorated billing handled automatically.</p>
        <h3>Upgrading</h3>
        <Steps
          items={[
            <>Go to <strong>Settings &gt; My Plan</strong>.</>,
            <>Pick the new plan.</>,
            <>Stripe charges the prorated difference for the remainder of your current cycle and the upgrade takes effect <strong>immediately</strong>.</>,
            <>You&apos;ll see the new feature limits and SMS/minute allotments right away.</>,
          ]}
        />
        <h3>Downgrading</h3>
        <Steps
          items={[
            <>From <strong>Settings &gt; My Plan</strong>, pick the lower-tier plan.</>,
            <>You keep your current plan until the end of the period you&apos;ve paid for. The downgrade takes effect at the start of your <strong>next billing cycle</strong> — no refund for the unused premium portion.</>,
            <>If your usage exceeds the new plan&apos;s limits when the downgrade applies, the app stays usable but new sends/calls fail until you upgrade or wait for the limit to reset.</>,
          ]}
        />
        <Tip>Need to downgrade due to seasonality (e.g. winter slowdown)? Plan it for the end of your billing cycle so you don&apos;t lose paid features mid-cycle.</Tip>
      </>
    ),
  },
  {
    id: "annual-vs-monthly",
    title: "Annual vs monthly billing",
    body: (
      <>
        <p>Pay monthly at the standard rate, or pay annually and the billing-cycle summary on the pricing page shows the year-total. The 25% Off First Year promo applies to all single-location plans automatically when active.</p>
        <h3>How annual billing is charged</h3>
        <ul>
          <li>The full annual amount is charged on day 1 of the billing cycle.</li>
          <li>Auto-renews on the same calendar day each year unless you cancel.</li>
          <li>Switching from monthly to annual mid-cycle: we credit the unused portion of your current monthly to your first annual charge automatically.</li>
        </ul>
        <h3>Switching annual → monthly</h3>
        <p>Switch from <strong>Settings &gt; My Plan</strong>. The change takes effect at the end of your annual term — we don&apos;t pro-rate refunds for the unused months.</p>
        <Tip>Annual makes sense once you&apos;re past the trial and confident in the product. The math: 12 monthly charges with the 25% promo equals 9 months of full price — same as the typical annual discount, but you keep monthly flexibility.</Tip>
      </>
    ),
  },
  {
    id: "cancelling",
    title: "Cancelling your subscription",
    body: (
      <>
        <p>Cancelling is one-click. We don&apos;t do retention pages, mandatory phone calls, or 30-day notice periods — we&apos;d rather you be happy than trapped.</p>
        <h3>How to cancel</h3>
        <Steps
          items={[
            <>Go to <strong>Settings &gt; My Plan</strong>.</>,
            <>Scroll to the bottom and click <strong>Cancel Subscription</strong>.</>,
            <>Confirm. The cancellation is recorded immediately.</>,
          ]}
        />
        <h3>What happens after</h3>
        <ul>
          <li>You keep full access through the end of the period you&apos;ve already paid for. Nothing is locked the moment you cancel.</li>
          <li>At the end of your paid period, your account drops to read-only — you can still view jobs, customers, and invoices, but you can&apos;t create new ones or send messages.</li>
          <li>Your data is retained for <strong>30 days</strong> in read-only mode. Resubscribe within that window and pick up exactly where you left off.</li>
          <li>After 30 days of cancellation, the account is queued for deletion per our <Link to="/privacy#retention">retention policy</Link>.</li>
        </ul>
        <Tip>Want to take a break without losing data? Email <a href="mailto:billing@fieldzai.com">billing@fieldzai.com</a> and ask about a pause — we offer up to 90 days at $0/month for active customers in the US.</Tip>
      </>
    ),
  },
  {
    id: "delete-account",
    title: "Deleting your account",
    body: (
      <>
        <p>If you want to remove all your data permanently — not just cancel the subscription — use account deletion.</p>
        <h3>How to delete</h3>
        <Steps
          items={[
            <>Go to <strong>Settings &gt; Account</strong>.</>,
            <>Scroll to the danger zone at the bottom and click <strong>Delete Account</strong>.</>,
            <>Confirm by typing your account email. This is intentionally a friction step — deletion is permanent.</>,
          ]}
        />
        <h3>What happens after</h3>
        <ul>
          <li>Your account is queued for deletion immediately. Within <strong>30 days</strong>, all personal data, customer records, jobs, invoices, call logs, and SMS history are permanently wiped from our database.</li>
          <li>Some records are retained longer for compliance — payment records (tax law), security/audit logs, and any data preserved under a legal hold. See <Link to="/privacy#retention">Privacy Policy &gt; Data Retention</Link> for the full breakdown.</li>
          <li>This is irreversible. Once the deletion completes, we cannot restore the account even with backups.</li>
        </ul>
        <Tip>Want to keep a copy of your data before deleting? Export customers, jobs, and invoices from each list page&apos;s <strong>Export CSV</strong> button before initiating the delete. The export gives you the full record set in a portable format.</Tip>
      </>
    ),
  },
];

const DocsBillingPage = () => (
  <DocsCategoryLayout
    category={{
      id: "billing",
      name: "Billing & Account",
      icon: CreditCard,
      intro: "Manage your subscription, billing cycle, plan changes, and account deletion. Everything self-serve.",
    }}
    articles={ARTICLES}
  />
);

export default DocsBillingPage;
